import { Button, Loader } from "@fanduel/formation-react-components";
import { ArrowsChevronDown } from "@fanduel/formation-react-icons";
import { Dropdown } from "@fanduel/og-fe-library";
import { useState, useMemo, useEffect } from "react";
import {
  ARTICLE_FILTER_APPLIED,
  AUTHOR_ARTICLES_EXPANDED,
} from "@/constants/amplitude";
import { ContentAuthor } from "@/graphql/gql/graphql";
import { useAmplitude } from "@/utils/hooks/useAmplitude";
import useRecentArticles from "@/utils/hooks/useRecentArticles";
import AuthorArticles from "./AuthorArticles";
import styles from "./authorArticlesFeed.module.scss";

type AuthorArticleFeedProps = {
  author: ContentAuthor;
};

const AuthorArticleFeed = ({ author }: AuthorArticleFeedProps) => {
  const options = useMemo(() => {
    const allOptions = [
      { label: "All", sportFilter: "", subCategoryFilter: "" },
    ];

    author.authoredSports?.forEach((sport) => {
      if (sport) {
        allOptions.push({
          label: sport.name,
          sportFilter: sport.name,
          subCategoryFilter: "",
        });
      }
    });

    author.authoredSubCategories?.forEach((subCategory) => {
      if (subCategory) {
        allOptions.push({
          label: subCategory.title || "Subcategory",
          sportFilter: subCategory.sport?.name || "",
          subCategoryFilter: subCategory.slug || "",
        });
      }
    });

    return allOptions;
  }, [author]);

  const [sportFilter, setSportFilter] = useState(options[0].sportFilter);
  const [subCategoryFilter, setSubCategoryFilter] = useState(
    options[0].subCategoryFilter
  );

  const [selected, setSelected] = useState(0);
  useEffect(() => {
    setSportFilter(options[0].sportFilter);
    setSubCategoryFilter(options[0].sportFilter);
    setSelected(0);
  }, [author, options]);

  const { articles, isFetching, fetchNextPage, hasNextPage } =
    useRecentArticles({
      limit: 16,
      filters: {
        author: { id: author.id },
        sportName: sportFilter,
        subCategorySlug: subCategoryFilter,
      },
    });

  const { logAmplitudeEvent } = useAmplitude();

  const logArticlesExpanded = () => {
    logAmplitudeEvent(AUTHOR_ARTICLES_EXPANDED, { Author: author.name });
  };

  const logFilterApplied = (opt: number) => {
    logAmplitudeEvent(ARTICLE_FILTER_APPLIED, {
      Author: author.name,
      "Filter Name": "category",
      "Filter Value": options[opt].label,
      Module: "article_filter",
    });
  };
  return (
    <div className={styles.container}>
      <h2 className={styles.title}>{"Their Analysis"}</h2>
      <div className={styles.dropdown}>
        <Dropdown
          title="Category"
          setSelected={setSelected}
          selected={selected}
          onChange={(opt) => {
            setSportFilter(options[opt].sportFilter);
            setSubCategoryFilter(options[opt].subCategoryFilter);
            logFilterApplied(opt);
          }}
          options={options}
        />
        {isFetching && (
          <div className={styles.buttonLoadingContainer}>
            <Loader size="sm" />
          </div>
        )}
      </div>

      <AuthorArticles articles={articles || []} />

      <div className={styles.btnContainer}>
        {hasNextPage && (
          <div className={styles.showMore}>
            <Button
              label="Show More News"
              variant="tertiary"
              type="button"
              iconRight={<ArrowsChevronDown />}
              onClick={() => {
                logArticlesExpanded();
                fetchNextPage();
              }}
              fill
              loading={isFetching}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default AuthorArticleFeed;
